import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp, useFetch } from '../store/app';
import { Fade, SearchBox, FilterSelect, BulkBar, BulkSelect, BulkAction, CheckBox } from './common';
import { Icon } from '../components/Icon';
import { Card, Button, Avatar, StatusBadge } from '../components/primitives';
import { st } from '../lib/status';
import { av } from '../lib/avatar';
import { api } from '../lib/api';
import { can } from '../lib/rbac';
import { useSelection } from '../lib/useSelection';
import type { Person } from '../../shared/types';

const COLS = '30px 2.4fr 1.4fr 1.4fr 1fr 0.8fr 40px';
const STAFF_STATUSES = ['active', 'onboarding', 'offboarding'];

export function People() {
  const { openDrawer, refresh, user } = useApp();
  const nav = useNavigate();
  const { data } = useFetch<Person[]>('/people');
  const [q, setQ] = useState('');
  const [type, setType] = useState('all');
  const [statusF, setStatusF] = useState('all');
  const people = data || [];

  const stats = useMemo(() => {
    const fte = people.filter((p) => p.type === 'FTE').length;
    const onboarding = people.filter((p) => p.status === 'onboarding' || p.status === 'offboarding').length;
    return [
      { label: 'Total people', value: String(people.length) },
      { label: 'FTE', value: String(fte) },
      { label: 'Contractors', value: String(people.length - fte) },
      { label: 'Onboarding', value: String(onboarding) },
    ];
  }, [people]);

  const statusOpts = useMemo(() => Array.from(new Set(people.map((p) => p.status))), [people]);
  const filtered = people.filter(
    (p) =>
      `${p.name} ${p.role} ${p.city}`.toLowerCase().includes(q.toLowerCase()) &&
      (type === 'all' || p.type === type) &&
      (statusF === 'all' || p.status === statusF),
  );
  const canEdit = user ? can(user.role, 'update', 'people') : false;
  const canDelete = user ? can(user.role, 'delete', 'people') : false;
  const canBulk = canEdit || canDelete;
  const sel = useSelection(filtered.map((p) => p.id));

  const bulkUpdate = async (patch: Record<string, string>) => {
    await api.post('/people/bulk', { action: 'update', ids: sel.ids, patch });
    sel.clear();
    refresh();
  };
  const bulkDelete = async () => {
    if (!confirm(`Delete ${sel.count} staff record${sel.count === 1 ? '' : 's'}? This can't be undone.`)) return;
    await api.post('/people/bulk', { action: 'delete', ids: sel.ids });
    sel.clear();
    refresh();
  };

  return (
    <Fade>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 16 }}>
        {stats.map((s) => (
          <Card key={s.label} style={{ padding: '14px 16px', borderRadius: 11 }}>
            <div style={{ fontSize: 12.5, color: 'var(--text-2)', fontWeight: 500 }}>{s.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-.02em', marginTop: 4 }}>{s.value}</div>
          </Card>
        ))}
      </div>
      <Card style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 16px', borderBottom: '1px solid var(--border)' }}>
          <SearchBox placeholder="Search people…" value={q} onChange={setQ} width={220} />
          <FilterSelect icon="badge" value={type} onChange={setType} options={[{ label: 'All types', value: 'all' }, { label: 'FTE', value: 'FTE' }, { label: 'Contractor', value: 'Contractor' }]} />
          <FilterSelect icon="filter_list" value={statusF} onChange={setStatusF} options={[{ label: 'All statuses', value: 'all' }, ...statusOpts.map((s) => ({ label: st(s).label, value: s }))]} />
          <div style={{ flex: 1 }} />
          {canEdit && <Button icon="person_add" onClick={() => openDrawer({ type: 'person', mode: 'create' })}>Add person</Button>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 14, padding: '10px 18px', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.04em', alignItems: 'center' }}>
          <div>{canBulk ? <CheckBox checked={sel.allChecked} indeterminate={sel.someChecked} onChange={sel.toggleAll} /> : null}</div>
          <div>Name</div><div>Role</div><div>Location</div><div>Type</div><div>Status</div><div />
        </div>
        {filtered.map((p) => {
          const avatar = av(p.name, p.avatar_kind);
          return (
            <div key={p.id} onClick={() => nav(`/people/${p.id}`)} className="cc-hover-s2 cc-clickable" style={{ display: 'grid', gridTemplateColumns: COLS, gap: 14, padding: '12px 18px', borderBottom: '1px solid var(--border)', alignItems: 'center', background: sel.has(p.id) ? 'var(--accent-bg)' : undefined }}>
              <div>{canBulk ? <CheckBox checked={sel.has(p.id)} onChange={() => sel.toggle(p.id)} /> : null}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
                <Avatar initials={avatar.in} bg={avatar.bg} fg={avatar.fg} size={34} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{p.day_rate || '—'}</div>
                </div>
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-2)' }}>{p.role}</div>
              <div style={{ fontSize: 13, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 5 }}><Icon name="location_on" size={16} color="var(--text-3)" />{[p.city, p.state].filter(Boolean).join(', ') || p.country}</div>
              <div><StatusBadge badge={st(p.type === 'FTE' ? 'fte' : 'contractor')} pill={false} /></div>
              <div><StatusBadge badge={st(p.status)} /></div>
              <div style={{ textAlign: 'right' }}>{canEdit && <span onClick={(e) => { e.stopPropagation(); openDrawer({ type: 'person', mode: 'edit', id: p.id }); }} style={{ cursor: 'pointer', display: 'inline-flex' }}><Icon name="edit" size={18} color="var(--text-3)" /></span>}</div>
            </div>
          );
        })}
      </Card>

      {canBulk && (
        <BulkBar count={sel.count} onClear={sel.clear}>
          {canEdit && (
            <>
              <BulkSelect label="Set status" icon="flag" onPick={(v) => bulkUpdate({ status: v })} options={STAFF_STATUSES.map((s) => ({ label: st(s).label, value: s }))} />
              <BulkSelect label="Set type" icon="badge" onPick={(v) => bulkUpdate({ type: v })} options={[{ label: 'FTE', value: 'FTE' }, { label: 'Contractor', value: 'Contractor' }]} />
            </>
          )}
          {canDelete && <BulkAction label="Delete" icon="delete" danger onClick={bulkDelete} />}
        </BulkBar>
      )}
    </Fade>
  );
}
